import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";

import { GET_LIST, GET_MANY, useClient } from "@peracto/client";

import { Link } from "react-router-dom";
import {
    HydraTable,
    Heading,
    TextFilter,
    StyledCheckbox,
    DateRangeFilter,
    SelectFilter,
} from "@peracto/peracto-ui";
import { Helmet } from "react-helmet";

const WarrantyList = () => {
    const { client } = useClient();
    const [countries, setCountries] = useState([]);
    const [selectedColumns, onSelectColumn] = useState({
        placeOfPurchase: false,
        dateOfPurchase: false,
        productNameAndModel: false,
        productAccessories: false,
        installedBy: false,
        installerName: false,
        modifiedAt: false,
    });

    const fetchCountries = async () => {
        try {
            const { data: countryData } = await client(GET_MANY, "countries", {
                id: "countries",
            });

            if (countryData?.length > 0 && Array.isArray(countryData)) {
                setCountries(countryData);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDate = (value, withTime = false) =>
        value
            ? dayjs(new Date(value)).format(
                  withTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD",
              )
            : "-";

    const fields = [
        {
            header: "Serial Number",
            accessor: "serialNumber",
            show: true,
            selectable: false,
            Cell: (row) => (
                <Link to={row.row.original.id}>{row.cell.value}</Link>
            ),
        },
        {
            header: "First Name",
            accessor: "firstName",
            show: true,
            selectable: false,
        },
        {
            header: "Last Name",
            accessor: "lastName",
            show: true,
            selectable: false,
        },
        {
            header: "Email",
            accessor: "email",
            show: true,
            selectable: false,
        },
        {
            header: "Place of Purchase",
            accessor: "placeOfPurchase",
            show: selectedColumns["placeOfPurchase"],
            selectable: true,
        },
        {
            header: "Date of Purchase",
            accessor: "dateOfPurchase",
            show: selectedColumns["dateOfPurchase"],
            selectable: true,
            Cell: ({ cell: { value } }) => formatDate(value),
        },
        {
            header: "Product Name & Model",
            accessor: "productNameAndModel",
            show: selectedColumns["productNameAndModel"],
            selectable: true,
        },

        {
            header: "Product Accessories",
            accessor: "productAccessories",
            show: selectedColumns["productAccessories"],
            selectable: true,
        },
        {
            header: "Installed By",
            accessor: "installedBy",
            show: selectedColumns["installedBy"],
            selectable: true,
        },
        {
            header: "Installer Name",
            accessor: "installerName",
            show: selectedColumns["installerName"],
            selectable: true,
        },
        {
            header: "Country Code",
            accessor: "countryCode",
            show: true,
            selectable: false,
            Cell: ({ cell: { value } }) => {
                if (!value) return "-";
                const country = countries.find((c) => c.iso3 === value);
                return country ? `${country.label} (${country.iso3})` : value;
            },
            Filter: (props) => (
                <SelectFilter
                    label="Country"
                    options={countries.map((country) => ({
                        label: `${country.label} (${country.iso3})`,
                        value: country.iso3,
                    }))}
                    {...props}
                />
            ),
        },
        {
            header: "Created At",
            accessor: "createdAt",
            show: true,
            selectable: false,
            Filter: (props) => (
                <DateRangeFilter label="Created At" {...props} />
            ),
            Cell: ({ cell: { value } }) => formatDate(value, true),
        },
        {
            header: "Modified At",
            accessor: "modifiedAt",
            show: selectedColumns["modifiedAt"],
            selectable: true,
            Filter: (props) => (
                <DateRangeFilter label="Modified At" {...props} />
            ),
            Cell: ({ cell: { value } }) => formatDate(value, true),
        },
    ];

    const columns = [
        {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
                <StyledCheckbox {...row.getToggleRowSelectedProps()} />
            ),
            selectable: false,
        },
    ];

    fields.forEach(
        ({ header, accessor, selectable, show, id = null, Cell, Filter }) =>
            columns.push({
                Header: header,
                accessor: accessor,
                sortType: "basic",
                Filter: Filter
                    ? Filter
                    : (props) => <TextFilter label={header} {...props} />,
                Cell: Cell
                    ? Cell
                    : ({ cell: { value } }) => (value ? value : "-"),
                selectable: selectable,
                show: show,
                id,
            }),
    );

    const onFetchData = (options) =>
        client(GET_LIST, "warranty-registrations", options);

    return (
        <>
            <Helmet>
                <title>Warranty Registrations | Peracto</title>
            </Helmet>
            <Heading name="Warranty Registrations">
                <div className="d-flex align-items-center ml-2">
                    <Link
                        className="btn btn-link mr-2"
                        to="/warranty-registrations/export"
                        data-testid="export-warranty-registrations"
                    >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Export Warranty Data
                    </Link>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WarrantyList;
